// UsersTab.js — Beta user-approval list.
// Expected to be removed or repurposed when Maia exits Beta and signups no
// longer require manual approval.

import React, { useState, useEffect, useMemo } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { db } from '../../FirebaseConfig';
import { collection, getDocs, updateDoc, doc } from 'firebase/firestore';
import { useLLMUsageByAllUsers } from '../../hooks/useAdminMetrics';

const USAGE_WINDOW_DAYS = 30;

const formatCost = (n) => `$${n.toFixed(2)}`;

const displayName = (u) => {
  const full = [u.first_name, u.last_name].filter(Boolean).join(' ').trim();
  return full || u.email || u.id;
};

const UsersTab = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [pendingUid, setPendingUid] = useState(null);
  const [detailUser, setDetailUser] = useState(null);

  const { byUid: usageByUid, loading: usageLoading } = useLLMUsageByAllUsers(USAGE_WINDOW_DAYS);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const userDocs = await getDocs(collection(db, 'users'));
        if (cancelled) return;
        setUsers(userDocs.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (e) {
        if (!cancelled) setError(e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const setApproved = async (userId, approved) => {
    setPendingUid(userId);
    try {
      await updateDoc(doc(db, 'users', userId), { approved });
      setUsers((prev) => prev.map((u) => (u.id === userId ? { ...u, approved } : u)));
    } finally {
      setPendingUid(null);
    }
  };

  const counts = useMemo(() => {
    const approved = users.filter((u) => u.approved).length;
    return { total: users.length, approved, pending: users.length - approved };
  }, [users]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return users
      .filter((u) => {
        if (filter === 'approved' && !u.approved) return false;
        if (filter === 'pending' && u.approved) return false;
        if (!q) return true;
        const hay = `${displayName(u)} ${u.email || ''}`.toLowerCase();
        return hay.includes(q);
      })
      .sort((a, b) => {
        // Pending first, then alphabetical by display name.
        if (!!a.approved !== !!b.approved) return a.approved ? 1 : -1;
        return displayName(a).localeCompare(displayName(b));
      });
  }, [users, filter, search]);

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="h6">User Approval</Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Chip size="small" label={`Total: ${counts.total}`} />
            <Chip size="small" color="success" variant="outlined" label={`Approved: ${counts.approved}`} />
            <Chip size="small" color="warning" variant="outlined" label={`Pending: ${counts.pending}`} />
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <ToggleButtonGroup
            size="small"
            value={filter}
            exclusive
            onChange={(_, v) => v && setFilter(v)}
          >
            <ToggleButton value="all">All</ToggleButton>
            <ToggleButton value="pending">Pending</ToggleButton>
            <ToggleButton value="approved">Approved</ToggleButton>
          </ToggleButtonGroup>
          <TextField
            size="small"
            placeholder="Search name or email"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ minWidth: 260 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {error ? (
          <Typography color="error">Failed to load users: {error.message}</Typography>
        ) : loading ? (
          <Typography variant="body2" color="text.secondary">Loading…</Typography>
        ) : visible.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {users.length === 0 ? 'No users found.' : 'No users match the current filter.'}
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Usage ({USAGE_WINDOW_DAYS}d)</TableCell>
                  <TableCell align="right">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visible.map((user) => {
                  const usage = usageByUid[user.id];
                  return (
                    <TableRow key={user.id} hover>
                      <TableCell>{displayName(user)}</TableCell>
                      <TableCell sx={{ color: 'text.secondary' }}>{user.email || '—'}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={user.approved ? 'Approved' : 'Pending'}
                          color={user.approved ? 'success' : 'warning'}
                          variant={user.approved ? 'filled' : 'outlined'}
                        />
                      </TableCell>
                      <TableCell>
                        {usageLoading ? (
                          <Typography variant="body2" color="text.secondary">…</Typography>
                        ) : usage ? (
                          <Button
                            size="small"
                            variant="text"
                            onClick={() => setDetailUser(user)}
                            sx={{ textTransform: 'none', p: 0, minWidth: 0 }}
                          >
                            {formatCost(usage.cost_usd)} · {usage.calls} calls
                          </Button>
                        ) : (
                          <Typography variant="body2" color="text.secondary">—</Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {user.approved ? (
                          <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            disabled={pendingUid === user.id}
                            onClick={() => setApproved(user.id, false)}
                          >
                            Remove Approval
                          </Button>
                        ) : (
                          <Button
                            size="small"
                            variant="contained"
                            disabled={pendingUid === user.id}
                            onClick={() => setApproved(user.id, true)}
                          >
                            Approve
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>

      <Dialog open={!!detailUser} onClose={() => setDetailUser(null)} fullWidth maxWidth="sm">
        <DialogTitle>
          Usage detail — {detailUser ? displayName(detailUser) : ''}
        </DialogTitle>
        <DialogContent dividers>
          {detailUser && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {detailUser.email || detailUser.id}
              </Typography>
              {(() => {
                const usage = usageByUid[detailUser.id];
                return (
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {usage
                      ? `${formatCost(usage.cost_usd)} across ${usage.calls} calls in the last ${USAGE_WINDOW_DAYS} days.`
                      : `No LLM activity in the last ${USAGE_WINDOW_DAYS} days.`}
                  </Typography>
                );
              })()}
              <Typography variant="body2" color="text.secondary">
                Per-user breakdowns (daily trend, by-function, by-model, call history) coming soon.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailUser(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default UsersTab;
